import { introHeaderReveal } from '@/config/section-header-reveal';
import { HERO_REVEAL_SELECTOR, HERO_SCENE_INTRO_ID } from '@/constants/heroScene';
import { appendSectionHeaderContent } from '@/motion/sectionHeaderContentReveal';

import type { ScrollSceneRuntime } from '@/motion/scrollScene';

type IntroContentRevealOptions = {
  runway: HTMLElement;
  /** Always boolean `true` — never a numeric scrub lag (freezes on iOS touch). */
  scrub: true;
};

type IntroContentRevealResult = {
  timeline: gsap.core.Timeline | null;
  cleanup: () => void;
};

/**
 * Chrome → localized SplitText headline units (+ aside in parallel) → body fade.
 * Scrubbed and bidirectional on the hero runway (all widths under motion-OK).
 * Scrolling back toward the hero reverses chrome, letters, and the body
 * (pin + prose) fade.
 *
 * Headline / aside / body are scoped to `#introduction` so chapter headers are
 * not pulled into this timeline. Timing from `introHeaderReveal`.
 */
export function createIntroContentReveal(
  runtime: ScrollSceneRuntime,
  { runway, scrub }: IntroContentRevealOptions,
): IntroContentRevealResult {
  const { gsap } = runtime;
  const chromeTargets = gsap.utils.toArray<HTMLElement>(HERO_REVEAL_SELECTOR);
  const contentRoot = document.getElementById(HERO_SCENE_INTRO_ID);

  if (chromeTargets.length === 0 && !contentRoot) {
    return { timeline: null, cleanup: () => undefined };
  }

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: runway,
      start: introHeaderReveal.timelineStart,
      end: introHeaderReveal.timelineEnd,
      scrub,
      invalidateOnRefresh: true,
    },
  });

  if (chromeTargets.length > 0) {
    timeline.fromTo(
      chromeTargets,
      { autoAlpha: 0, y: introHeaderReveal.chrome.offsetY },
      {
        autoAlpha: 1,
        y: 0,
        ease: introHeaderReveal.chrome.ease,
        duration: introHeaderReveal.chrome.weight,
      },
      0,
    );
  }

  let contentCleanup: (() => void) | null = null;

  if (contentRoot) {
    const headlineAt = timeline.duration();
    const { hasTargets, cleanup } = appendSectionHeaderContent(runtime, {
      root: contentRoot,
      timeline,
      timing: introHeaderReveal,
      headlineAt,
    });
    contentCleanup = cleanup;

    // Body may start before headline finishes; if chrome-only and no content
    // targets, still allow chrome-only timeline.
    if (!hasTargets && chromeTargets.length === 0) {
      contentCleanup();
      timeline.scrollTrigger?.kill();
      timeline.kill();
      return { timeline: null, cleanup: () => undefined };
    }
  }

  return {
    timeline,
    cleanup: () => {
      contentCleanup?.();
      timeline.scrollTrigger?.kill();
      timeline.kill();
    },
  };
}
